import React from 'react';
import { UnpackNestedValue, useFormContext } from 'react-hook-form';
import { useStateMachine } from 'little-state-machine';
import useSelector from '@redux/typedHooks';
import S from '../../../styles/becomeAMember/StyledBecomeAMember';
import { useGTMDataLayer } from '../../../hooks/GTM/useGTMDataLayer';
import { NAFGTMEvent } from '../../../../../types/GTM/NAFGTMEvent';
import { updateAction } from '../lib/updateAction';
import { useProductData } from './Summary/Summary.utils';
import { sendAddPaymentInfoEvent } from '../../../utils/Tracking/Purchase/AddPaymentInfo';
import { ECommerceItem } from '../../../../../types/GTM/ECommerceItem';
import {
  didUseVippsMembershipRegistrationMethod,
  getVippsMembershipRegistrationMethodTrackingPayload,
} from './VippsModal/VippsModal.utils';

export type WizardNavigationProps = {
  currentStep: number;
  totalSteps: number;
  previousStep: () => void;
  nextStep: () => void;
  isValid: boolean;
  onSubmit: (e: UnpackNestedValue<any>) => void | boolean | Promise<void | boolean>;
  goToStep?: (step: number) => void;
  onGoback?: (e: UnpackNestedValue<any>) => void;
  getValues?: () => UnpackNestedValue<any>;
  trackingStep?: number;
};

export const WizardNavigation = ({
  currentStep,
  totalSteps,
  previousStep,
  nextStep,
  isValid,
  onSubmit,
  onGoback,
  getValues,
  trackingStep,
}: Partial<WizardNavigationProps>) => {
  const dataLayer = useGTMDataLayer();
  const checkoutStep = trackingStep ?? currentStep;
  const currentForm = useFormContext();
  const { handleSubmit, formState } = currentForm || {};
  const {
    state: { membershipForm, internationalDrivingPermitForm },
  } = useStateMachine({ updateAction });

  const campaignCode = useSelector((state) => state.becomeAMember?.data?.campaignCodeDetails?.campaignCode);

  // Handle membership flow
  const membershipProduct = useSelector((state) =>
    membershipForm?.product?.productId
      ? state.becomeAMember.data.products[membershipForm.product.productId]
      : undefined,
  );

  const { mainProductPrice, addonProductsPrice } = useProductData({
    products: { mainProduct: membershipForm?.product },
  });

  // Handle IDP flow
  const idpProduct = useSelector((state) =>
    internationalDrivingPermitForm?.product?.productId
      ? state.internationalDrivingPermit.data.eligibleProducts?.[internationalDrivingPermitForm.product.productId]
      : undefined,
  );

  const onNextHandler = async (e) => {
    const shouldContinue = onSubmit ? await onSubmit(e) : undefined;
    window.scrollTo(0, 0);

    if (shouldContinue === false) {
      return;
    }

    if (nextStep) {
      if (checkoutStep) {
        dataLayer?.push({
          event: NAFGTMEvent.checkoutAction,
          checkout_step: checkoutStep,
          checkout_action: currentStep && totalSteps && currentStep !== totalSteps - 1 ? 'Neste' : 'Gå til betaling',
          ...(campaignCode && { campaign_code: campaignCode }),
        });

        // Fire add_payment_info event when going to payment.
        // Since we use the same form for both membership and IDP, we handle them slightly differently.
        if (currentStep && totalSteps && currentStep === totalSteps - 1) {
          let items: ECommerceItem[] = [];
          let totalValue = 0;

          // Handle membership flow.
          if (membershipProduct && membershipForm?.product) {
            totalValue = addonProductsPrice + mainProductPrice;
            items = [
              {
                item_id: membershipProduct.productNumber,
                quantity: membershipForm.product.amount?.toString() || '1',
                item_name: membershipProduct.productName,
                price:
                  membershipForm.product.amount && membershipProduct.productAppPrice[membershipForm.product.amount]
                    ? (
                        membershipProduct.productAppPrice[membershipForm.product.amount].itemTotalPrice /
                        membershipForm.product.amount
                      ).toFixed(2)
                    : membershipProduct.productAppPrice[1]?.itemTotalPrice.toFixed(2) || '0.00',
                item_category2: 'Medlemskap',
                item_category3: 'Innmelding',
                // Add item_variant when it is merged!
              },
            ];
            // Handle IDP
          } else if (idpProduct && internationalDrivingPermitForm?.product) {
            const productPrice = idpProduct.productAppPrice[1]?.itemTotalPrice || 0;
            totalValue = productPrice;
            items = [
              {
                item_id: idpProduct.productNumber,
                quantity: '1',
                item_name: idpProduct.productName,
                price: productPrice.toFixed(2),
                item_category2: 'Internasjonalt Førerkort',
                item_category3: 'Bestill',
              },
            ];
          }

          if (items.length > 0 && totalValue > 0) {
            sendAddPaymentInfoEvent({
              dataLayer,
              items,
              value: totalValue,
              checkoutRegistrationMethod: membershipProduct
                ? getVippsMembershipRegistrationMethodTrackingPayload(didUseVippsMembershipRegistrationMethod())
                    .checkout_registration_method
                : undefined,
              campaignCode,
            });
          }
        }
      }
      nextStep();
    }
  };

  const onNext = handleSubmit ? handleSubmit(onNextHandler) : onNextHandler;

  return (
    <S.NavigationButtons>
      <S.Buttons>
        {currentStep !== 1 && (
          <S.StyledButton
            variant="outlined"
            onClick={() => {
              if (getValues && onGoback && previousStep) {
                const values = getValues();
                onGoback(values);
                previousStep();
              } else if (previousStep) previousStep();
              if (checkoutStep) {
                dataLayer?.push({
                  event: NAFGTMEvent.checkoutAction,
                  checkout_step: checkoutStep,
                  checkout_action: 'Tilbake',
                  ...(campaignCode && { campaign_code: campaignCode }),
                });
              }
            }}
            disabled={currentStep === 1}
          >
            Tilbake
          </S.StyledButton>
        )}
        <S.StyledButton onClick={onNext} disabled={currentStep === totalSteps || !isValid || formState?.isSubmitting}>
          {currentStep && totalSteps && currentStep !== totalSteps - 1 ? 'Neste' : 'Gå til betaling'}
        </S.StyledButton>
      </S.Buttons>
    </S.NavigationButtons>
  );
};
